import React, { useMemo, useState } from 'react';
import {
	Badge,
	Button,
	Col,
	Empty,
	Row,
	Spin,
	Tag,
	Tabs,
	Tooltip,
	Typography,
} from 'antd';
import {
	ClockCircleOutlined,
	EditOutlined,
	FileTextOutlined,
	LinkOutlined,
	PlusOutlined,
	UserOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import ContentModal from './ContentModal';
import FeedbackPanel from './FeedbackPanel';
import IssuesPanel from './IssuesPanel';
import ProgrammingStatsFormModal from './ProgrammingStatsFormModal';
import ReviewsPanel from './ReviewsPanel';
import { authorName, programPartLabel } from './authors';
import { hasRichTextContent } from './fields';
import { formatProgrammingCaption } from './programmingStatus';
import {
	useGetProgramQuery,
	useGetProgramsQuery,
} from '../../redux/api/manufacturing';
import {
	PROGRAM_TYPE_LABELS,
	programTypeAllowsMultiplePrograms,
	programTypeUsesFilename,
	type ProgramBasedOnSummary,
	type ProgramType,
} from '../../interfaces/CamProgramming.interface';

import './styles.css';

const { Title, Text } = Typography;

const OLI_PROGRAM_TABS: { key: ProgramType; label: string }[] = [
	{ key: 'cam', label: 'CAM Program' },
	{ key: 'keyence', label: 'Keyence Program' },
	{ key: 'inspection_plan', label: 'Inspection Plan' },
];

/** One tile of the summary strip: a label, the headline, and a caption. */
const StatCard: React.FC<{
	icon: React.ReactNode;
	label: string;
	value: React.ReactNode;
	/** Sets the headline in prose rather than as a number. */
	text?: boolean;
	suffix?: string;
	caption?: React.ReactNode;
	action?: React.ReactNode;
}> = ({ icon, label, value, text, suffix, caption, action }) => (
	<div className="cam-metric">
		<div className="cam-metric__head">
			<span className="cam-metric__label">
				{icon}
				{label}
			</span>
			{action}
		</div>
		<div
			className={`cam-metric__value${text ? ' cam-metric__value--text' : ''}`}
		>
			{value}
			{suffix && <span className="cam-metric__suffix">{suffix}</span>}
		</div>
		{caption && <div className="cam-metric__caption">{caption}</div>}
	</div>
);

/** Tab label carrying the count the collapsible headers used to show. */
const TabLabel: React.FC<{
	title: string;
	count: number;
	/** Issues use a red count pill so they stand out from reviews and feedback. */
	variant?: 'default' | 'issues';
}> = ({ title, count, variant = 'default' }) => (
	<span className="cam-subtab">
		{title}
		<span
			className={
				variant === 'issues'
					? 'cam-section__count cam-section__count--issues'
					: 'cam-section__count'
			}
		>
			{count}
		</span>
	</span>
);

/** Top-level program type tab with a red issue count when any are logged. */
const ProgramCategoryTabLabel: React.FC<{
	label: string;
	issuesCount: number;
}> = ({ label, issuesCount }) => (
	<span className="cam-program-tab">
		{label}
		{issuesCount > 0 && (
			<Badge count={issuesCount} size="small" className="cam-program-tab__badge" />
		)}
	</span>
);

/**
 * Starting point for a line item nobody has scored yet. Creating the program
 * is what attributes it, so the form asks only for the stats — the server
 * records the signed-in user as the programmer.
 */
const LogProgrammingStats: React.FC<{
	orderLineItemId: string;
	orderLineItemName?: string | null;
	programType: ProgramType;
	onStatsLogged?: () => void;
}> = ({ orderLineItemId, orderLineItemName, programType, onStatsLogged }) => {
	const [modalOpen, setModalOpen] = useState(false);

	return (
		<>
			<Empty
				description={`No ${PROGRAM_TYPE_LABELS[programType].toLowerCase()} stats logged for this part yet`}
			>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setModalOpen(true)}
				>
					Log Programming Stats
				</Button>
			</Empty>

			<ProgrammingStatsFormModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				programType={programType}
				orderLineItemId={orderLineItemId}
				orderLineItemName={orderLineItemName}
				onSuccess={onStatsLogged}
			/>
		</>
	);
};

/**
 * Scoring for a program that already exists. The strip up top answers who
 * wrote it and what it has cost so far, while the tabs below hold the prose:
 * the reviews, the issues they turned up, and general feedback.
 */
const ProgramScoring: React.FC<{
	programId: string;
	siblingPrograms?: Pick<ProgramBasedOnSummary, 'id' | 'filename'>[];
	onStatsLogged?: () => void;
}> = ({ programId, siblingPrograms = [], onStatsLogged }) => {
	const { data: program, isLoading } = useGetProgramQuery(programId);
	const [editOpen, setEditOpen] = useState(false);
	const [notesOpen, setNotesOpen] = useState(false);

	if (isLoading || !program) {
		return (
			<div className="cam-section__placeholder">
				<Spin />
			</div>
		);
	}

	// Who wrote the program, and what it took them. Everything a review cost
	// is rolled into the totals beside it rather than itemized here — the
	// reviews themselves are a tab away.
	const programmerName =
		program.programmer?.full_name || program.programmer?.email || 'Unassigned';
	const programmingCaption =
		formatProgrammingCaption(program, { pendingLabel: 'Not completed' }) ?? '';

	// What the part has cost in programmer time. The review total comes from
	// the server rather than from the loaded page of reviews, so a program with
	// more reviews than fit on one page still adds up.
	const programmingMinutes = program.programming_duration_minutes ?? 0;
	const reviewMinutes = program.total_review_minutes ?? 0;
	const programFilename = programTypeUsesFilename(program.program_type)
		? program.filename?.trim() ||
			program.order_line_item.name ||
			'Unnamed program'
		: null;
	const basedOnLabel =
		program.based_on_program?.filename?.trim() || null;

	return (
		<>
			{programFilename && (
				<Title level={5} style={{ marginBottom: basedOnLabel ? 4 : 12 }}>
					<FileTextOutlined style={{ marginRight: 8 }} />
					{programFilename}
				</Title>
			)}
			{basedOnLabel && (
				<Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
					Based on: {basedOnLabel}
				</Text>
			)}
			{/* Without this, editing a shared program from one part's page
			    looks like it only affects that part. */}
			{program.linked_line_item_count > 1 && (
				<Tooltip
					title={program.order_line_items
						.map((lineItem) => lineItem.name || 'Unnamed part')
						.join(', ')}
				>
					<Tag
						icon={<LinkOutlined />}
						color="blue"
						style={{ marginBottom: 12 }}
					>
						Shared with {program.linked_line_item_count - 1} other line{' '}
						{program.linked_line_item_count === 2 ? 'item' : 'items'}
					</Tag>
				</Tooltip>
			)}
			<Row gutter={[12, 12]}>
				<Col xs={24} md={8}>
					<StatCard
						icon={<UserOutlined />}
						label="Programmer"
						value={programmerName}
						text
						caption={programmingCaption}
						action={
							<span className="cam-metric__actions">
								{hasRichTextContent(program.programmer_feedback) && (
									<Tooltip title="View programmer notes">
										<button
											type="button"
											className="cam-tag-button"
											onClick={() => setNotesOpen(true)}
										>
											<Tag
												icon={<FileTextOutlined />}
												color="purple"
												style={{ margin: 0 }}
											>
												Notes
											</Tag>
										</button>
									</Tooltip>
								)}
								<Tooltip title="Edit programming stats">
									<Button
										type="text"
										size="small"
										icon={<EditOutlined />}
										onClick={() => setEditOpen(true)}
									/>
								</Tooltip>
							</span>
						}
					/>
				</Col>
				<Col xs={12} md={8}>
					<StatCard
						icon={<ClockCircleOutlined />}
						label="Total time"
						value={(programmingMinutes + reviewMinutes).toLocaleString()}
						suffix="min"
						caption={`${programmingMinutes} programming · ${reviewMinutes} review`}
					/>
				</Col>
				<Col xs={12} md={8}>
					<StatCard
						icon={<WarningOutlined />}
						label="Total issues"
						value={program.issues_count.toLocaleString()}
						caption={
							program.issues_count > 0
								? `across ${program.reviews_count} review${
										program.reviews_count === 1 ? '' : 's'
								  }`
								: 'None reported'
						}
					/>
				</Col>
			</Row>

			{/* The tab bar labels each panel and its own rule separates it from
			    the roll-up above, so the panels drop their own headers. */}
			<Tabs
				className="cam-subtabs"
				size="small"
				items={[
					{
						key: 'reviews',
						label: <TabLabel title="Reviews" count={program.reviews_count} />,
						children: (
							<ReviewsPanel
								programId={program.id}
								allowReviewerOverride={false}
								chrome="bare"
							/>
						),
					},
					{
						key: 'issues',
						label: (
							<TabLabel
								title="Issues"
								count={program.issues_count}
								variant="issues"
							/>
						),
						children: (
							<IssuesPanel programId={program.id} chrome="bare" />
						),
					},
					{
						key: 'feedback',
						label: <TabLabel title="Feedback" count={program.feedback_count} />,
						children: (
							<FeedbackPanel programId={program.id} chrome="bare" />
						),
					},
				]}
			/>

			<ProgrammingStatsFormModal
				open={editOpen}
				onClose={() => setEditOpen(false)}
				programType={program.program_type}
				orderLineItemId={program.order_line_item.id}
				programId={program.id}
				initialProgrammerId={program.programmer?.id ?? null}
				pinnedProgrammer={program.programmer ?? null}
				initialFilename={program.filename}
				initialDurationMinutes={program.programming_duration_minutes}
				initialStartedAt={program.programming_started_at ?? null}
				initialCompletedAt={program.programming_completed_at}
				initialProgrammerFeedback={program.programmer_feedback}
				existingPrograms={siblingPrograms}
				onSuccess={onStatsLogged}
			/>

			<ContentModal
				title={`Programmer notes — ${programPartLabel(
					program.order_line_item.name || 'Program',
					program.filename
				)}`}
				meta={
					<Text type="secondary">
						{authorName(program.programmer, 'Unassigned')}
					</Text>
				}
				html={program.programmer_feedback}
				emptyText="No notes"
				open={notesOpen}
				onClose={() => setNotesOpen(false)}
			/>
		</>
	);
};

const ProgramScoringTabContent: React.FC<{
	orderLineItemId: string;
	orderLineItemName?: string | null;
	programType: ProgramType;
	onStatsLogged?: () => void;
}> = ({ orderLineItemId, orderLineItemName, programType, onStatsLogged }) => {
	const { data, isLoading } = useGetProgramsQuery({
		order_line_item: orderLineItemId,
		program_type: programType,
		ordering: 'created_time',
	});
	const [addModalOpen, setAddModalOpen] = useState(false);

	const programs = data?.results ?? [];
	const allowMultiplePrograms = programTypeAllowsMultiplePrograms(programType);
	const existingProgramsForModal =
		programType === 'cam'
			? programs.map((program) => ({
					id: program.id,
					filename: program.filename,
				}))
			: [];

	if (isLoading) {
		return (
			<div className="cam-section__placeholder">
				<Spin />
			</div>
		);
	}

	if (programs.length === 0) {
		return (
			<LogProgrammingStats
				orderLineItemId={orderLineItemId}
				orderLineItemName={orderLineItemName}
				programType={programType}
				onStatsLogged={onStatsLogged}
			/>
		);
	}

	return (
		<>
			{programs.map((program, index) => (
				<div
					key={program.id}
					className={index > 0 ? 'cam-program-stack__item' : undefined}
				>
					<ProgramScoring
						programId={program.id}
						siblingPrograms={programs
							.filter((row) => row.id !== program.id)
							.map((row) => ({
								id: row.id,
								filename: row.filename,
							}))}
						onStatsLogged={onStatsLogged}
					/>
				</div>
			))}

			{allowMultiplePrograms && (
				<div className="cam-program-stack__actions">
					<Button
						type="dashed"
						icon={<PlusOutlined />}
						onClick={() => setAddModalOpen(true)}
					>
						Log Another Program
					</Button>
				</div>
			)}

			{allowMultiplePrograms && (
				<ProgrammingStatsFormModal
					open={addModalOpen}
					onClose={() => setAddModalOpen(false)}
					programType={programType}
					orderLineItemId={orderLineItemId}
					orderLineItemName={orderLineItemName}
					existingPrograms={existingProgramsForModal}
					onSuccess={onStatsLogged}
				/>
			)}
		</>
	);
};

/**
 * Program scoring for one order line item, split by the kind of program being
 * scored (CAM, Keyence, or Inspection Plan).
 */
const ProgramScoringCard: React.FC<{
	orderLineItemId: string;
	orderLineItemName?: string | null;
	onStatsLogged?: () => void;
}> = ({ orderLineItemId, orderLineItemName, onStatsLogged }) => {
	const [activeTab, setActiveTab] = useState<ProgramType>('cam');
	const { data: programsData } = useGetProgramsQuery({
		order_line_item: orderLineItemId,
	});

	const issuesCountByType = useMemo(() => {
		const counts: Record<ProgramType, number> = {
			cam: 0,
			keyence: 0,
			inspection_plan: 0,
		};
		for (const program of programsData?.results ?? []) {
			counts[program.program_type] += program.issues_count;
		}
		return counts;
	}, [programsData?.results]);

	return (
		<>
			<Title level={4} style={{ marginBottom: 8 }}>
				Program Scoring
			</Title>

			<Tabs
				type="card"
				size="small"
				activeKey={activeTab}
				onChange={(key) => setActiveTab(key as ProgramType)}
				tabBarStyle={{ marginBottom: 0 }}
				items={OLI_PROGRAM_TABS.map(({ key, label }) => ({
					key,
					label: (
						<ProgramCategoryTabLabel
							label={label}
							issuesCount={issuesCountByType[key]}
						/>
					),
					children: (
						<div className="w-full bg-white border-b border-r border-l p-6">
							<ProgramScoringTabContent
								orderLineItemId={orderLineItemId}
								orderLineItemName={orderLineItemName}
								programType={key}
								onStatsLogged={onStatsLogged}
							/>
						</div>
					),
				}))}
			/>
		</>
	);
};

export default ProgramScoringCard;
